import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { insertRow, listRows, updateRow } from "@/lib/adminApi";
import {
  SUBMISSION_STATUS,
  metaFor,
  optionsFrom,
  type FarmerStory,
  type StorySubmission,
} from "@/lib/adminContent";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnAccent,
  btnGhost,
  btnPrimary,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

function buildExcerpt(text: string, max = 190): string {
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max - 1).trim()}…` : clean;
}

function readTimeFor(text: string): string {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 200))} min read`;
}

export default function AdminStorySubmissions() {
  const [rows, setRows] = useState<StorySubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<StorySubmission>("story_submissions", "submitted_at"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load the submissions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<StorySubmission>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("story_submissions", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const approve = async (submission: StorySubmission, publish: boolean) => {
    setBusy(true);
    setError("");
    try {
      const storyText = submission.story || "";
      const created = await insertRow<FarmerStory>("farmer_stories", {
        title: submission.title,
        farmer_name: submission.submitter_name,
        farm_name: null,
        village: submission.village || null,
        category: submission.category || "Farm Life",
        excerpt: buildExcerpt(storyText),
        content: storyText,
        cover_image: null,
        read_time: readTimeFor(storyText),
        gallery: [],
        featured: false,
        status: publish ? "published" : "draft",
        published_at: publish ? new Date().toISOString() : null,
      });
      await updateRow("story_submissions", submission.id, {
        status: "approved",
        story_id: created.id,
      });
      setRows((list) =>
        list.map((row) => (row.id === submission.id ? { ...row, status: "approved", story_id: created.id } : row)),
      );
      notify(
        publish
          ? `"${submission.title}" is now live in the journal.`
          : `"${submission.title}" saved as a draft story.`,
      );
      setSelectedId(null);
    } catch {
      setError("We couldn't turn that submission into a story. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!query) return true;
      return [row.title, row.submitter_name, row.email, row.village || "", row.category || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, statusFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<StorySubmission>[] = [
    {
      key: "title",
      header: "Submission",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 line-clamp-1">{row.title}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.submitter_name} · {row.email}</p>
        </div>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (row) => (
        <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full whitespace-nowrap">
          {row.category || "—"}
        </span>
      ),
    },
    {
      key: "village",
      header: "Village",
      render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.village || "—"}</span>,
    },
    {
      key: "submitted",
      header: "Received",
      render: (row) => (
        <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.submitted_at)}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => <StatusPill {...metaFor(SUBMISSION_STATUS, row.status)} />,
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="Story Submissions"
        subtitle="Reader stories shared from the website. Read them, then approve the best ones into the journal."
        actions={
          <Link to="/admin/farmer-stories" className={btnGhost}>
            <i className="ri-book-open-line"></i> Farmer Stories
          </Link>
        }
      />

      <StatCards
        stats={[
          { label: "Total submissions", value: rows.length, icon: "ri-inbox-line", tone: "primary" },
          { label: "New", value: rows.filter((row) => row.status === "pending").length, icon: "ri-mail-unread-line", tone: "accent" },
          { label: "Reading", value: rows.filter((row) => row.status === "review").length, icon: "ri-eye-line", tone: "secondary" },
          { label: "Approved", value: rows.filter((row) => row.status === "approved").length, icon: "ri-checkbox-circle-line", tone: "primary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by title, name, email, village…" />
          <Select
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: "all", label: "All statuses" }, ...optionsFrom(SUBMISSION_STATUS)]}
          />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} submissions</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading submissions…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon="ri-inbox-line"
            title={rows.length === 0 ? "No submissions yet" : "No submissions match"}
            message={
              rows.length === 0
                ? "Stories shared on the Share Your Story page will arrive here for review."
                : "Try a different search or clear the filters."
            }
          />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.title || "Submission"}
        subtitle="Reader story"
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => approve(selected, true)} disabled={busy} className={btnPrimary}>
                {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
                Approve &amp; publish
              </button>
              <button type="button" onClick={() => approve(selected, false)} disabled={busy} className={btnAccent}>
                <i className="ri-draft-line"></i> Approve to drafts
              </button>
              <button
                type="button"
                onClick={() => update(selected.id, { status: "rejected" }, "Submission declined.")}
                className={btnGhost}
              >
                <i className="ri-close-line"></i> Decline
              </button>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(SUBMISSION_STATUS, selected.status)} />
              {selected.category ? (
                <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full">
                  {selected.category}
                </span>
              ) : null}
              {selected.story_id ? (
                <span className="text-xs font-semibold text-primary-700 bg-primary-100 border border-primary-300 px-2.5 py-1 rounded-full">
                  Story #{selected.story_id}
                </span>
              ) : null}
            </div>

            <DetailList
              items={[
                { icon: "ri-user-3-line", label: "Submitted by", value: selected.submitter_name },
                { icon: "ri-mail-line", label: "Email", value: selected.email },
                { icon: "ri-phone-line", label: "Phone", value: selected.phone || "—" },
                { icon: "ri-map-pin-line", label: "Village", value: selected.village || "—" },
                { icon: "ri-calendar-line", label: "Received", value: formatDate(selected.submitted_at) },
              ]}
            />

            <Field label="Story">
              <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                {selected.story || "—"}
              </p>
            </Field>

            {selected.story_id ? (
              <Link to="/admin/farmer-stories" className={btnGhost}>
                <i className="ri-book-open-line"></i> Open in Farmer Stories
              </Link>
            ) : null}

            <Field label="Editorial notes">
              <textarea
                rows={3}
                value={selected.admin_notes || ""}
                onChange={(event) =>
                  setRows((list) =>
                    list.map((row) => (row.id === selected.id ? { ...row, admin_notes: event.target.value } : row)),
                  )
                }
                onBlur={() => update(selected.id, { admin_notes: selected.admin_notes })}
                placeholder="Consent notes, edits needed, follow-up…"
                className="w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
              ></textarea>
            </Field>

            <button
              type="button"
              onClick={() => update(selected.id, { status: "review" }, "Marked as reading.")}
              className={btnGhost}
            >
              <i className="ri-eye-line"></i> Mark as reading
            </button>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Approving a submission creates a farmer story you can still edit before it goes live.
      </p>
      {node}
    </div>
  );
}